import { useCallback, useEffect, useRef, useState } from 'react';
import { Receipt, Upload, Search, ExternalLink, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import PageShell from '../components/PageShell';
import DataTable, { type Column } from '../components/DataTable';
import StatusPill from '../components/StatusPill';
import FilterBar, {
  controlClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../components/FilterBar';
import {
  uploadReceipt,
  searchDocuments,
  getDocumentOcr,
  type DocumentSearchResultItem,
  type DocumentUploadResponse,
} from '../lib/api';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const columns: Column<DocumentSearchResultItem>[] = [
  {
    key: 'title',
    header: 'Document',
    render: (r) =>
      r.url ? (
        <a
          href={r.url}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1 text-zt-primary hover:text-zt-accent"
        >
          {r.title} <ExternalLink size={13} />
        </a>
      ) : (
        r.title
      ),
  },
  { key: 'snippet', header: 'Snippet', render: (r) => r.snippet || '—' },
  {
    key: 'source',
    header: 'Source',
    render: (r) => <StatusPill status={r.source} tone="neutral" label={r.source} />,
  },
];

function UploadStatus({ result }: { result: DocumentUploadResponse | null }) {
  if (!result) return null;
  const organized = result.destination && result.status !== 'ocr_complete';
  return (
    <div className="mt-4 rounded-lg border border-zt-border bg-zt-bg/40 p-4 text-sm text-slate-200">
      <div className="flex flex-wrap items-center gap-2">
        <FileText size={16} className="text-zt-primary" />
        <span className="font-medium text-white">Last upload</span>
        {result.ocrStatus && (
          <StatusPill
            status={result.ocrStatus}
            tone={result.ocrStatus === 'complete' ? 'success' : 'neutral'}
            label={result.ocrStatus === 'complete' ? 'OCR complete' : 'OCR processing…'}
          />
        )}
      </div>
      <dl className="mt-3 grid gap-2 sm:grid-cols-2">
        {result.documentType && (
          <>
            <dt className="text-slate-400">Type</dt>
            <dd>{result.documentType}</dd>
          </>
        )}
        {result.destination && (
          <>
            <dt className="text-slate-400">Destination</dt>
            <dd>{organized ? `Organized to ${result.destination}` : result.destination}</dd>
          </>
        )}
        {result.ocrProvider && (
          <>
            <dt className="text-slate-400">OCR provider</dt>
            <dd>{result.ocrProvider}</dd>
          </>
        )}
      </dl>
      {result.ocrTextPreview && (
        <p className="mt-3 rounded border border-zt-border/60 bg-black/20 p-3 text-xs leading-relaxed text-slate-300">
          {result.ocrTextPreview}
        </p>
      )}
    </div>
  );
}

function Receipts() {
  const [results, setResults] = useState<DocumentSearchResultItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('receipt');
  const [uploading, setUploading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState('');
  const [lastUpload, setLastUpload] = useState<DocumentUploadResponse | null>(null);
  const [amount, setAmount] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async (searchText: string) => {
    if (!searchText.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const result = await searchDocuments(searchText.trim(), 25);
    if (result.ok && result.data) setResults(result.data.data.results ?? []);
    else setResults([]);
    setLoading(false);
  }, []);

  useEffect(() => {
    load('receipt');
  }, [load]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setUploadPhase('Uploading…');
    setLastUpload(null);
    try {
      const base64 = await fileToBase64(file);
      setUploadPhase('OCR processing…');
      const result = await uploadReceipt({
        file_name: file.name,
        content_base64: base64,
        amount: amount.trim() || undefined,
      });
      if (result.ok && result.data) {
        if (result.data.status === 'not_configured') {
          toast('Paperless is not configured');
        } else {
          setLastUpload(result.data);
          if (result.data.ocrId) {
            const ocr = await getDocumentOcr(result.data.ocrId);
            if (ocr.ok && ocr.data?.text && !result.data.ocrTextPreview) {
              setLastUpload((prev) =>
                prev
                  ? {
                      ...prev,
                      ocrTextPreview: ocr.data!.text.slice(0, 500),
                    }
                  : prev
              );
            }
          }
          const dest = result.data.destination;
          if (dest) {
            toast.success(`Organized to ${dest.split('/').slice(-2).join('/')}`);
          } else {
            toast.success('Receipt uploaded');
          }
          setAmount('');
          load(query);
        }
      }
    } catch {
      toast.error('Could not read file');
    } finally {
      setUploading(false);
      setUploadPhase('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <PageShell
      title="Documents & Receipts"
      subtitle="Uploads are OCR'd automatically, classified, and routed to oCIS folders. Search includes indexed OCR text."
    >
      <div className="rounded-xl border border-zt-border bg-zt-surface p-6">
        <div className="flex items-center gap-2">
          <Receipt className="text-zt-primary" size={18} />
          <h3 className="font-semibold text-white">Upload a document</h3>
        </div>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="mb-1 block text-sm font-medium text-slate-200">Amount (optional)</label>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="42.50"
              className={`w-full ${controlClass}`}
            />
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file);
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className={primaryButtonClass}
          >
            <Upload size={16} /> {uploading ? uploadPhase || 'Uploading…' : 'Choose file & upload'}
          </button>
        </div>
        <UploadStatus result={lastUpload} />
      </div>

      <FilterBar
        actions={
          <button type="button" onClick={() => load(query)} className={secondaryButtonClass}>
            <Search size={16} /> Search
          </button>
        }
      >
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load(query)}
          placeholder="Search documents (OCR text indexed)…"
          className={`min-w-[220px] flex-1 ${controlClass}`}
        />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={results}
        rowKey={(r, i) => `${r.source}-${r.title}-${i}`}
        loading={loading}
        emptyMessage="No documents found."
      />
    </PageShell>
  );
}

export default Receipts;
